import {
  RiApps2AiLine,
  RiFileLine,
  RiFilterLine,
  RiFingerprintLine,
  RiFolderLine,
  RiInboxArchiveLine,
  RiSparklingLine,
} from '@remixicon/react';

import { IconComponent } from '@backstage/frontend-plugin-api';

import {
  IconBusiness,
  IconChartBar,
  IconChartLine,
  IconEvents,
  IconFlag,
  IconGridView,
  IconLab,
  IconStations,
  IconUser,
  RolloutIcon,
} from '@spotify/confidence-core-react';

export type Module =
  | 'metrics'
  | 'abtests'
  | 'rollouts'
  | 'flags'
  | 'segments'
  | 'events'
  | 'analysis'
  | 'insights'
  | 'explore'
  | 'surfaces'
  | 'initiatives'
  | 'issues'
  | 'collections'
  | 'projects'
  | 'entities';

export const ModuleIcons: Record<Module, IconComponent> = {
  metrics: IconChartLine as IconComponent,
  flags: IconFlag as IconComponent,
  abtests: IconLab as IconComponent,
  rollouts: RolloutIcon as IconComponent,
  events: IconEvents as IconComponent,
  analysis: IconChartBar as IconComponent,
  insights: IconStations as IconComponent,
  explore: RiSparklingLine as IconComponent,
  segments: IconUser as IconComponent,
  surfaces: IconGridView as IconComponent,
  initiatives: IconBusiness as IconComponent,
  issues: RiInboxArchiveLine as IconComponent,
  collections: RiFolderLine as IconComponent,
  projects: RiApps2AiLine as unknown as IconComponent,
  entities: RiFingerprintLine as IconComponent,
};

export interface ModuleStyle {
  Icon: IconComponent;
  color: string;
  label: string;
}

export const MODULE_STYLES: Record<Module, ModuleStyle> = {
  metrics: { Icon: ModuleIcons.metrics, color: '#06B6D4', label: 'Metric' },
  abtests: { Icon: ModuleIcons.abtests, color: '#8B5CF6', label: 'AB test' },
  rollouts: { Icon: ModuleIcons.rollouts, color: '#F59E0B', label: 'Rollout' },
  flags: { Icon: ModuleIcons.flags, color: '#E74C3C', label: 'Flag' },
  segments: { Icon: ModuleIcons.segments, color: '#EC4899', label: 'Segment' },
  events: { Icon: ModuleIcons.events, color: '#10B981', label: 'Event' },
  analysis: { Icon: ModuleIcons.analysis, color: '#3B82F6', label: 'Analysis' },
  insights: { Icon: ModuleIcons.insights, color: '#A855F7', label: 'Insight' },
  explore: { Icon: ModuleIcons.explore, color: '#FACC15', label: 'Explore' },
  surfaces: { Icon: ModuleIcons.surfaces, color: '#0EA5E9', label: 'Surface' },
  initiatives: {
    Icon: ModuleIcons.initiatives,
    color: '#64748B',
    label: 'Initiative',
  },
  issues: {
    Icon: ModuleIcons.issues,
    color: '#0F766E',
    label: 'Issue',
  },
  collections: {
    Icon: ModuleIcons.collections,
    color: '#94A3B8',
    label: 'Collection',
  },
  projects: { Icon: ModuleIcons.projects, color: '#6366F1', label: 'Project' },
  entities: { Icon: ModuleIcons.entities, color: '#78716C', label: 'Entity' },
};

const DOCUMENT_STYLE: ModuleStyle = {
  Icon: RiFileLine as unknown as IconComponent,
  color: '#6366F1',
  label: 'Document',
};

const RULE_STYLE: ModuleStyle = {
  Icon: RiFilterLine as unknown as IconComponent,
  color: '#0D9488',
  label: 'Rule',
};

const stripPrefix = (path: string): string =>
  path
    .replace(/^\/\//, '')
    .replace(/^\/confidence(?=\/)/, '')
    .replace(/^\//, '');

export const pathToModuleStyle = (path: string): ModuleStyle | undefined => {
  const normalized = stripPrefix(path);

  if (normalized.startsWith('workflows/abtest/')) return MODULE_STYLES.abtests;
  if (normalized.startsWith('workflows/rollout/'))
    return MODULE_STYLES.rollouts;
  if (
    normalized.startsWith('workflows/analysis/') ||
    normalized.startsWith('workflows/reanalysis/')
  ) {
    return MODULE_STYLES.analysis;
  }
  if (normalized.startsWith('rules/') || normalized.includes('/rules/')) {
    return RULE_STYLE;
  }
  if (normalized.startsWith('flags/')) return MODULE_STYLES.flags;
  if (normalized.startsWith('events/')) return MODULE_STYLES.events;
  if (normalized.startsWith('metrics/')) return MODULE_STYLES.metrics;
  if (normalized.startsWith('issues/')) return MODULE_STYLES.issues;
  if (normalized.startsWith('segments/')) return MODULE_STYLES.segments;
  if (normalized.startsWith('collaborativeDocuments/')) return DOCUMENT_STYLE;
  if (normalized.startsWith('externalDocuments/')) {
    return DOCUMENT_STYLE;
  }

  return undefined;
};

export const workflowToIcon = (workflow: string): IconComponent => {
  switch (workflow) {
    default:
    case 'abest':
      return ModuleIcons.abtests;
    case 'rollout':
      return ModuleIcons.rollouts;
    case 'analysis':
      return ModuleIcons.analysis;
  }
};

export const resourceToIcon = (resource: string): IconComponent | undefined => {
  switch (resource) {
    case 'flag':
      return ModuleIcons.flags;
    case 'event':
      return ModuleIcons.events;
    case 'metric':
      return ModuleIcons.metrics;
    case 'issue':
      return ModuleIcons.issues;
    case 'abtest':
      return ModuleIcons.abtests;
    case 'rollout':
      return ModuleIcons.rollouts;
    case 'analysis':
      return ModuleIcons.analysis;
    default:
      return undefined;
  }
};
